import Phaser from 'phaser';
import { getLevel } from '../levels';
import { chapterName, chapterTagline, CHAPTERS, codenameOf, getChapter } from '../levels/manifest';
import { Progress, computeUnlocks } from '../progress';
import { Backdrop } from '../render/Backdrop';
import { Sfx } from '../audio/sfx';
import { drawNodeMark } from '../render/nodeMark';
import { drawWatermark } from '../render/watermark';
import { t } from '../i18n';
import { SANS, TYPE, UI, Panel, padlock } from '../render/theme';
import type { NodeType } from '../sim/types';

/**
 * ChapterScene — shows the 6 slots in a chapter as a vertical list of cards.
 * Tapping a real, unlocked slot starts that level. Placeholders are visually
 * "TBA". Locked levels show a padlock.
 */
export class ChapterScene extends Phaser.Scene {
  /** Android back button action, read by back-button.ts. */
  onBack?: () => void;
  private chapterId!: string;
  private sfx!: Sfx;
  private _dragDist = 0;

  constructor() {
    super('Chapter');
  }

  init(data: { chapterId: string }) {
    this.chapterId = data.chapterId;
  }

  create() {
    this.sfx = (this.registry.get('sfx') as Sfx | undefined) ?? new Sfx();
    this.registry.set('sfx', this.sfx);
    this.sfx.init();

    this.onBack = () => { this.sfx.tap(); this.scene.start('Menu'); };

    const ch = getChapter(this.chapterId);
    if (!ch) {
      this.scene.start('Menu');
      return;
    }

    // Glow stays faint: an additive circle at full tint floods the screen.
    new Backdrop(this, {
      glow: 0.06, scanlines: false, particles: false,
      glowTint: ch.accent,
    });
    const { width, height } = this.scale;
    const M = 72;
    const contentW = width - M * 2;
    const headerStart = this.children.list.length;

    // === HEADER === back is a real button, then chapter name and progress
    const back = this.add.text(M, 76, `‹  ${t('sectors')}`, {
      fontFamily: SANS, fontSize: `${TYPE.body}px`, color: UI.accentHex, fontStyle: '700',
    });
    const backHit = this.add.rectangle(M + 120, 90, 300, 132, 0xffffff, 0)
      .setInteractive({ useHandCursor: true });
    backHit.on('pointerover', () => { back.setColor('#ffffff'); this.sfx.ui(); });
    backHit.on('pointerout', () => back.setColor(UI.accentHex));
    backHit.on('pointerup', () => { if (this._dragDist > 12) return; this.sfx.tap(); this.scene.start('Menu'); });

    this.add.text(M, 168, `CH.${String(ch.num).padStart(2, '0')}`, {
      fontFamily: SANS, fontSize: `${TYPE.label}px`, color: ch.accentHex, fontStyle: '700',
    });
    this.add.text(M, 208, chapterName(ch), {
      fontFamily: SANS, fontSize: `${TYPE.title + 8}px`, color: UI.text, fontStyle: '800',
    });
    this.add.text(M, 304, chapterTagline(ch), {
      fontFamily: SANS, fontSize: `${TYPE.body - 2}px`, color: UI.textDim,
    });

    const realIds = ch.slots.filter((s): s is string => !!s);
    const solvedCount = realIds.filter(id => Progress.isSolved(id)).length;
    const realCount = realIds.length;
    const pct = realCount > 0 ? solvedCount / realCount : 0;
    const barY = 378;
    const bar = this.add.graphics();
    bar.fillStyle(UI.hairline, 1);
    bar.fillRoundedRect(M, barY - 4, contentW - 150, 8, 4);
    if (pct > 0) {
      bar.fillStyle(ch.accent, 1);
      bar.fillRoundedRect(M, barY - 4, Math.max(8, (contentW - 150) * pct), 8, 4);
    }
    this.add.text(width - M, barY, `${solvedCount} / ${realCount}`, {
      fontFamily: SANS, fontSize: `${TYPE.label + 2}px`, color: ch.accentHex, fontStyle: '700',
    }).setOrigin(1, 0.5);

    // === LEVEL GRID === two columns of tiles, scannable at a glance
    const unlocks = computeUnlocks(CHAPTERS);
    const gap = 24;
    const tileW = (contentW - gap) / 2;
    const tileH = 250;
    const top = 440;
    // Everything created so far is the header. For a long chapter the grid scrolls
    // under it, so the header is pinned and sits on an opaque strip.
    const rows = Math.ceil(ch.slots.length / 2);
    const gridBottom = top + rows * (tileH + gap);
    const maxScroll = Math.max(0, gridBottom + 40 - height);
    if (maxScroll > 0) {
      const strip = this.add.graphics().setScrollFactor(0).setDepth(59);
      strip.fillStyle(0x070b18, 1);
      strip.fillRect(0, 0, width, top - 20);
      strip.lineStyle(1, UI.hairline, 1);
      strip.lineBetween(0, top - 20, width, top - 20);
      for (const o of this.children.list.slice(headerStart)) {
        const g = o as Phaser.GameObjects.GameObject & { setScrollFactor?: (n: number) => unknown; setDepth?: (n: number) => unknown };
        if (g === strip) continue;
        g.setScrollFactor?.(0);
        g.setDepth?.(60);
      }
      strip.setDepth(59);
      this.cameras.main.setBounds(0, 0, width, gridBottom + 40);
      let dragging = false, lastPy = 0;
      this.input.on('pointerdown', (p: Phaser.Input.Pointer) => { dragging = true; lastPy = p.y; this._dragDist = 0; });
      this.input.on('pointerup', () => { dragging = false; });
      this.input.on('pointerupoutside', () => { dragging = false; });
      this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
        if (!dragging) return;
        const dy = p.y - lastPy; lastPy = p.y;
        this._dragDist += Math.abs(dy);
        this.cameras.main.scrollY = Phaser.Math.Clamp(this.cameras.main.scrollY - dy, 0, maxScroll);
      });
      this.input.on('wheel', (_p: unknown, _go: unknown, _dx: number, dy: number) => {
        this.cameras.main.scrollY = Phaser.Math.Clamp(this.cameras.main.scrollY + dy * 0.6, 0, maxScroll);
      });
    }

    ch.slots.forEach((slotId, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = M + col * (tileW + gap);
      const y = top + row * (tileH + gap);
      if (slotId === null) return;
      this.makeLevelTile(x, y, tileW, tileH, slotId, ch, i + 1, !!unlocks[slotId]);
    });

    drawWatermark(this);
  }

  private makeLevelTile(
    x: number, y: number, w: number, h: number,
    levelId: string,
    ch: typeof CHAPTERS[number],
    slotNum: number,
    unlocked: boolean,
  ) {
    const level = getLevel(levelId);
    if (!level) return;
    const solved = Progress.isSolved(levelId);
    const accent = solved ? UI.signal : (unlocked ? ch.accent : UI.hairline);
    const accentHex = solved ? UI.signalHex : (unlocked ? ch.accentHex : UI.textFaint);

    const panel = new Panel(this, x, y, w, h, {
      fill: unlocked ? UI.surface : 0x080d1c,
      stroke: accent, strokeAlpha: unlocked ? 0.8 : 0.5,
    });

    // Big level number, the thing the eye lands on
    this.add.text(x + 32, y + 22, String(slotNum).padStart(2, '0'), {
      fontFamily: SANS, fontSize: '84px', color: unlocked ? accentHex : UI.textFaint, fontStyle: '800',
    });

    if (!unlocked) {
      padlock(this, x + w - 56, y + 56, 0x4a5a82);
      this.add.text(x + 32, y + h - 62, t('locked'), {
        fontFamily: SANS, fontSize: `${TYPE.label + 2}px`, color: UI.textFaint, fontStyle: '700',
      });
      return;
    }

    // Status chip, top right
    this.add.text(x + w - 32, y + 36, solved ? '✓ ' + t('cleared') : t('open'), {
      fontFamily: SANS, fontSize: `${TYPE.label}px`, color: accentHex, fontStyle: '700',
    }).setOrigin(1, 0);

    this.add.text(x + 32, y + h - 112, codenameOf(levelId), {
      fontFamily: SANS, fontSize: '32px', color: UI.text, fontStyle: '800',
    });

    const uniqueTypes = Array.from(new Set(level.nodes.map((n) => n.type)))
      .filter((tp): tp is NodeType => tp !== 'normal');
    uniqueTypes.slice(0, 6).forEach((tp, j) => {
      const g = this.add.graphics();
      g.setPosition(x + 44 + j * 44, y + h - 40);
      drawNodeMark(g, tp, 18);
      g.setAlpha(solved ? 0.6 : 0.95);
    });

    const hit = this.add.rectangle(x + w / 2, y + h / 2, w, h, 0xffffff, 0)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerover', () => {
      this.sfx.ui();
      panel.restyle({ stroke: accent, strokeAlpha: 1, strokeWidth: 3 });
    });
    hit.on('pointerout', () => panel.restyle({ stroke: accent, strokeAlpha: 0.8, strokeWidth: 2 }));
    hit.on('pointerup', () => {
      if (this._dragDist > 12) return;
      this.sfx.tap();
      this.cameras.main.flash(110, 20, 40, 80);
      this.time.delayedCall(110, () => this.scene.start('Game', { levelId, chapterId: this.chapterId }));
    });
  }
}
